import React, { useImperativeHandle } from "react";
import type { JSX } from "react";

import { Box, boxForwardRef } from "../../../Box/Box";
import { useInputMask } from "../../hooks/useInputMask";
import { useInputStore } from "../../store/inputStore";
import { useCoreInputTextStore } from "../../store/store";
import { createTestIdForCoreInputTextElement } from "../../utils/getTestId";

import type { BoxComponentWithDefault } from "../../../Box/Box";
import type { IInputBlockProps, IInputMaskedProps } from "../../types";

import classes from "./InputBlock.styl";

type BoxInputType<T = unknown> = BoxComponentWithDefault<
    "input",
    IInputBlockProps & T
>;

const INPUT_BLOCK_TEST_ID = createTestIdForCoreInputTextElement("input-block");

/**
 * Основной блок с Input'ом. Тут находится логика выбора нужного поля (с маской или без)
 */
export const InputBlock = boxForwardRef<BoxInputType>(
    (
        {
            as: InputComponent = "input",
            placeholder,
            isHintExisted,
            ...inputProps
        },
        ref,
    ): JSX.Element => {
        const { inputId, state, hintId } = useCoreInputTextStore();
        const { inputRef, onChange, value, mask } = useInputStore();

        /** Устанавливаем ref, полученному от родителя, значение от inputRef.current */
        useImperativeHandle(ref, () => inputRef.current);

        const commonProps = (() => {
            const result = {
                id: inputId,
                className: classes["input"],
                readOnly: state === "readonly",
                disabled: state === "disabled",
                ref: inputRef,
                ...inputProps,
            };
            if (isHintExisted) {
                Object.assign(result, {
                    "aria-describedby": hintId,
                });
            }
            return result;
        })();

        if (!mask) {
            return (
                <Input
                    as={InputComponent}
                    {...commonProps}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    data-tsid={INPUT_BLOCK_TEST_ID}
                    ref={inputRef}
                />
            );
        }

        return (
            <InputMasked
                as={InputComponent}
                {...commonProps}
                value={value}
                onChange={onChange}
                mask={mask}
                data-tsid={INPUT_BLOCK_TEST_ID}
                ref={inputRef}
            />
        );
    },
);

const Input = boxForwardRef<BoxInputType>(
    ({ as: InputComponent = "input", "data-tsid": tsid, ...props }, ref) => {
        return (
            <Box as={InputComponent} {...props} data-tsid={tsid} ref={ref} />
        );
    },
);

const INPUT_MASKED_PLACEHOLDER = createTestIdForCoreInputTextElement(
    "input-masked-placeholder",
);

const InputMasked = boxForwardRef<BoxInputType<IInputMaskedProps>>(
    (
        {
            as: InputComponent = "input",
            mask,
            "data-tsid": tsid,
            value,
            onChange,
            ...props
        },
        ref,
    ) => {
        const { maskPlaceholder } = useInputMask({
            ref,
            value,
            mask,
            onChange,
        });

        return (
            <>
                <Input
                    as={InputComponent}
                    {...props}
                    data-tsid={tsid}
                    ref={ref}
                />
                <div
                    className={classes["mask-placeholder"]}
                    data-tsid={INPUT_MASKED_PLACEHOLDER}
                >
                    <span>{maskPlaceholder.value}</span>
                    {maskPlaceholder.placeholder}
                </div>
            </>
        );
    },
);
